import { getLocale } from '@borealis/aurora';
import { Trans } from '@lingui/react/macro';
import { useMutation } from '@tanstack/react-query';
import '@warp-ds/components/icon';
import { Button } from '@warp-ds/react';
import { useCallback, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import type { ImageWidgetModel } from '../../../../../types/widget.js';
import { batchArray } from '../../../../utils.js';
import { MAX_IMAGES, OTHER_FILE_SPECIFIERS, VALID_FILE_EXTENSIONS } from '../constants.js';
import type { ImageUploadConfig } from '../types/index.js';
import { DeleteAllModal } from './delete-all-modal.js';
import { DescriptionModal } from './description-modal.js';
import { ImageAlerts } from './image-alerts.js';
import { SortableImageGrid } from './sortable-image-grid.js';

interface ImageFormValue {
  uri: string;
  width: number;
  height: number;
  description?: string;
  rotation?: number;
}

type ImageUploadWidgetProps = Omit<ImageWidgetModel, 'type' | 'label'> & {
  config: ImageUploadConfig;
};

export const ImageUploadWidget = ({ name, config, className }: ImageUploadWidgetProps) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploadingImages, setUploadingImages] = useState<any[]>([]);
  // Unified alert state for all image related errors
  const [alert, setAlert] = useState<
    null | 'UploadError' | 'InvalidFileType' | 'FileTooLarge' | 'TooManyImages' | 'RotateError'
  >(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);
  const [rotatingImageId, setRotatingImageId] = useState<string | undefined>(undefined);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { watch, setValue, getValues } = useFormContext();
  const images = (watch(name) || []) as ImageFormValue[];

  const clearErrors = () => {
    setAlert(null);
  };

  // Upload mutation for handling image uploads
  const uploadImageMutation = useMutation({
    mutationFn: async ({ file, key }: { file: File; key: string }) => {
      const formData = new FormData();
      formData.append('image', file);

      const response = await fetch(
        `${config.root}/api/upload/image/${config.itemId}${config.orgId ? `?orgId=${config.orgId}` : ''}`,
        {
          method: 'POST',
          body: formData,
          headers: {
            'x-csrf-token': config.csrfToken,
          },
        },
      );

      if (!response.ok) {
        if (response.status === 415) {
          throw new Error('InvalidFileType');
        }
        if (response.status === 413) {
          throw new Error('FileTooLarge');
        }
        // Any other non-ok response should be retried based on retry predicate
        throw new Error(`UploadError:${response.status}`);
      }

      return { ...(await response.json()), key };
    },
    // Retry up to 3 times except for known client errors
    retry: (failureCount, error) => {
      const message = (error as Error).message || '';
      if (message === 'InvalidFileType' || message === 'FileTooLarge') return false;
      return failureCount < 3;
    },
    retryDelay: config.retryDelayMs ?? ((attempt) => 2 ** attempt * 1000),
    onSuccess: (data) => {
      // Remove from uploading state
      setUploadingImages((prev) => prev.filter((img) => img.key !== data.key));

      // Add to form
      const { path, width, height } = data;
      const newImage: ImageFormValue = { uri: path, width, height };
      addImageToForm(newImage);
    },
    onError: (error, { key }) => {
      // Remove from uploading state
      setUploadingImages((prev) => prev.filter((img) => img.key !== key));

      // Set specific alert based on error message
      if (error.message === 'InvalidFileType') {
        setAlert('InvalidFileType');
      } else if (error.message === 'FileTooLarge') {
        setAlert('FileTooLarge');
      } else {
        setAlert('UploadError');
      }
    },
  });

  // Rotate mutation for handling image rotation
  const rotateImageMutation = useMutation({
    mutationFn: async (imageUri: string) => {
      const formData = new FormData();
      formData.append('path', imageUri);

      const response = await fetch(
        `${config.root}/api/upload/rotate/${config.itemId}${config.orgId ? `?orgId=${config.orgId}` : ''}`,
        {
          method: 'POST',
          body: formData,
          headers: {
            'x-csrf-token': config.csrfToken,
          },
        },
      );

      if (!response.ok) {
        throw new Error('Failed to rotate image');
      }

      return await response.json();
    },
    onMutate: (imageUri) => {
      // Optimistic update - immediately mark as rotating
      setRotatingImageId(imageUri);
    },
    onSuccess: (data, imageUri) => {
      // Update the image in the form with new rotated data
      const currentImages = (getValues(name) || []) as ImageFormValue[];
      const imageIndex = currentImages.findIndex((i) => i.uri === imageUri);

      if (imageIndex !== -1) {
        const imageToRotate = currentImages[imageIndex];
        const rotatedImage = {
          ...imageToRotate,
          uri: data.path,
          width: data.width,
          height: data.height,
          rotation: 0,
        };

        const updatedImages = [
          ...currentImages.slice(0, imageIndex),
          rotatedImage,
          ...currentImages.slice(imageIndex + 1),
        ];
        setValue(name, updatedImages, { shouldValidate: true });
      }
    },
    onSettled: (_data, error) => {
      // Always clear rotating state when done (success or error)
      setRotatingImageId(undefined);

      // Set rotate error if mutation failed
      if (error) {
        setAlert('RotateError');
      } else {
        // Clear rotate error on success if it was previously set
        if (alert === 'RotateError') clearErrors();
      }
    },
  });

  // Use a callback to get the latest form values when adding a new image
  const addImageToForm = useCallback(
    (newImage: ImageFormValue) => {
      // Get the current images directly from form to ensure we have the latest state
      const currentImages = (getValues(name) || []) as ImageFormValue[];

      // Check if this image is already in the form (by URI)
      const isDuplicate = currentImages.some((img) => img.uri === newImage.uri);
      if (isDuplicate) {
        return;
      }

      // Check if adding this image would exceed the maximum
      if (currentImages.length >= MAX_IMAGES) {
        setAlert('TooManyImages');
        return;
      }

      setValue(name, [...currentImages, newImage], { shouldValidate: true });

      // Clear too many images error if we're now within limits
      if (alert === 'TooManyImages' && currentImages.length + 1 <= MAX_IMAGES) clearErrors();
    },
    [getValues, setValue, name],
  );

  const handleFiles = async (files: FileList | File[]) => {
    clearErrors();

    const currentImages = (getValues(name) || []) as ImageFormValue[];
    const fileArray = Array.from(files);

    // Check if adding these files would exceed the maximum
    if (currentImages.length + fileArray.length > MAX_IMAGES) {
      setAlert('TooManyImages');
      return;
    }

    // Check for invalid file types
    const invalidFiles = fileArray.filter(
      (file) => !VALID_FILE_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext)),
    );

    if (invalidFiles.length > 0) {
      setAlert('InvalidFileType');
      return;
    }

    if (alert === 'TooManyImages') clearErrors();

    const newSlots = fileArray.map((file) => {
      const key = uuidv4();
      const previewUrl = URL.createObjectURL(file);
      return { key, file, status: 'uploading' as const, previewUrl };
    });
    setUploadingImages((prev) => [...prev, ...newSlots]);

    // Upload files in batches of 2 to reduce memory and network load
    const uploadBatches = batchArray(newSlots, 2);

    const uploadBatch = async (batch: typeof newSlots) => {
      const promises = batch.map((slot) => uploadImageMutation.mutateAsync({ file: slot.file, key: slot.key }));
      await Promise.all(promises);
    };

    // Process batches sequentially
    for (const batch of uploadBatches) {
      try {
        await uploadBatch(batch);
      } catch {
        // Individual upload errors are handled by the mutation's onError callback
        // Continue processing remaining batches even if current batch has errors
        if (uploadBatches.length > 1) setAlert('UploadError');
      }
    }
  };

  const handleDelete = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    setValue(name, newImages, { shouldValidate: true });

    // Clear too many images error if we're now within limits
    if (newImages.length <= MAX_IMAGES && alert === 'TooManyImages') clearErrors();
  };

  const handleReorder = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    const newImages = [...images];
    const [moved] = newImages.splice(oldIndex, 1);
    newImages.splice(newIndex, 0, moved);
    setValue(name, newImages, { shouldValidate: true });
  };

  const handleEditDescription = (idx: number) => {
    setEditingIdx(idx);
    setModalOpen(true);
  };

  const handleSaveDescriptions = (changedDescriptions: { index: number; description: string }[]) => {
    // Update only the changed descriptions
    const newImages = [...images];
    changedDescriptions.forEach(({ index, description }) => {
      if (newImages[index]) {
        newImages[index] = { ...newImages[index], description };
      }
    });
    setValue(name, newImages, { shouldValidate: true });

    setModalOpen(false);
    setEditingIdx(null);
  };

  const handleDismissModal = () => {
    setModalOpen(false);
    setEditingIdx(null);
  };

  const getImageUrl = (imageUri: string) => {
    if (imageUri.startsWith('blob:')) {
      return imageUri;
    }
    return `${config.cdnUrl}/1280w/${imageUri}`;
  };

  const hasImages = images.length > 0 || uploadingImages.length > 0;

  return (
    <div className={`${className} w-full`}>
      <div className="mb-16 flex items-center justify-between">
        <label htmlFor="image-upload" className="text-s font-bold cursor-pointer">
          <Trans id="mobility-item-insertion.image.label" comment="Label for the section where images are uploaded">
            Images
          </Trans>
          <span className="pl-8 s-text-subtle font-normal">
            (
            <Trans
              id="mobility-item-insertion.optional.label"
              comment="Used in a input field label to show that the field is optional"
            >
              optional
            </Trans>
            )
          </span>
        </label>
        {hasImages && (
          <div>
            <Button
              link
              quiet
              className="flex items-center gap-8"
              onClick={() => setDeleteAllModalOpen(true)}
              data-testid="delete-all-images"
            >
              <w-icon name="Bin" size="small" locale={getLocale()} />
              <Trans
                id="mobility-item-insertion.form.images.delete-all"
                comment="Text on button for deleting all images"
              >
                Delete all photos
              </Trans>
            </Button>
            <DeleteAllModal
              open={deleteAllModalOpen}
              onCancel={() => setDeleteAllModalOpen(false)}
              onDelete={() => {
                setValue(name, [], { shouldValidate: true });
                if (alert === 'TooManyImages') clearErrors();
                clearErrors();
                setDeleteAllModalOpen(false);
              }}
            />
          </div>
        )}
      </div>
      <SortableImageGrid
        slots={[
          ...images.map((img: ImageFormValue, idx: number) => ({
            key: img.uri,
            status: 'done' as const,
            uploadedFile: {
              path: img.uri,
              width: img.width,
              height: img.height,
            },
            description: img.description,
            rotation: img.rotation,
            onEditDescription: () => handleEditDescription(idx),
            onRotate: async () => {
              if (!img.uri || img.uri.startsWith('blob:')) {
                return;
              }
              if (alert === 'RotateError') clearErrors();
              rotateImageMutation.mutate(img.uri);
            },
            isRotating: rotateImageMutation.isPending && rotatingImageId === img.uri,
          })),
          ...uploadingImages.map((slot) => ({
            ...slot,
            isRotating: false,
          })),
        ]}
        onDelete={(key) => {
          const index = images.findIndex((img: ImageFormValue) => img.uri === key);
          if (index !== -1) handleDelete(index);
        }}
        onReorder={handleReorder}
        onFilesDrop={handleFiles}
        disabled={modalOpen}
      />
      <div
        className={`w-full rounded-4 border-1 border-dashed flex flex-col items-center justify-center s-bg outline-none mt-16${isDragActive ? ' s-border-black' : ''}${hasImages ? ' p-16' : ' h-[178]'} hidden sm:flex`}
        style={{ position: 'static' }}
        role="application"
        onDragEnter={(e) => {
          e.preventDefault();
          dragCounter.current++;
          setIsDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          dragCounter.current--;
          if (dragCounter.current === 0) {
            setIsDragActive(false);
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          dragCounter.current = 0;
          setIsDragActive(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <div
          className={`w-full h-full flex flex-col items-center justify-center${hasImages ? ' md:flex-row md:items-center md:justify-start' : ''}`}
        >
          {hasImages ? (
            <>
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-0 border-2 rounded-8 flex items-center gap-8 md:mr-16"
              >
                <w-icon name="Plus" size="small" locale={getLocale()} />
                <Trans
                  id="mobility-item-insertion.form.images.add-more-photos"
                  comment="Text on button for adding more photos"
                >
                  Add more photos
                </Trans>
              </Button>
              <p className="flex items-center justify-center h-32 text-s-text-neutral mb-8 md:mb-0">
                {isDragActive ? (
                  <Trans
                    id="mobility-item-insertion.form.imageUpload.dropPhotosHere"
                    comment="Text shown when dragging photos over dropzone"
                  >
                    Drop the photos here
                  </Trans>
                ) : (
                  <Trans
                    id="mobility-item-insertion.form.imageUpload.dragPhotosHereToUpload"
                    comment="Text shown in dropzone when images exist"
                  >
                    or drag photos here to upload
                  </Trans>
                )}
              </p>
            </>
          ) : (
            <>
              <p className="flex items-center justify-center h-32 text-s-text-neutral mb-8">
                {isDragActive ? (
                  <Trans
                    id="mobility-item-insertion.form.imageUpload.dropPhotosHere"
                    comment="Text shown when dragging photos over dropzone"
                  >
                    Drop the photos here
                  </Trans>
                ) : (
                  <Trans
                    id="mobility-item-insertion.form.imageUpload.dragPhotosHere"
                    comment="Text shown in empty dropzone"
                  >
                    Drag photos here to upload
                  </Trans>
                )}
              </p>
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-0 border-2 rounded-8 flex items-center gap-8"
              >
                <w-icon name="Plus" size="small" locale={getLocale()} />
                <Trans id="mobility-item-insertion.form.images.add-photos" comment="Text on button for adding photos">
                  Add photos
                </Trans>
              </Button>
            </>
          )}
        </div>
      </div>
      {!hasImages && (
        <div className="flex sm:hidden w-full justify-center">
          <Button
            type="button"
            fullWidth
            onClick={() => fileInputRef.current?.click()}
            className="mt-0 border-2 rounded-8 flex items-center gap-8 w-full"
            data-testid="add-photos"
          >
            <w-icon name="Plus" size="small" locale={getLocale()} />
            <Trans id="mobility-item-insertion.form.images.add-photos" comment="Text on button for adding photos">
              Add photos
            </Trans>
          </Button>
        </div>
      )}
      {hasImages && (
        <div className="flex sm:hidden w-full justify-center mt-16">
          <Button
            type="button"
            fullWidth
            onClick={() => fileInputRef.current?.click()}
            className="mt-0 border-2 rounded-8 flex items-center gap-8 w-full"
            data-testid="add-more-photos"
          >
            <w-icon name="Plus" size="small" locale={getLocale()} />
            <Trans
              id="mobility-item-insertion.form.images.add-more-photos"
              comment="Text on button for adding more photos"
            >
              Add more photos
            </Trans>
          </Button>
        </div>
      )}
      <ImageAlerts alert={alert} />
      <input
        ref={fileInputRef}
        type="file"
        accept={VALID_FILE_EXTENSIONS.concat(OTHER_FILE_SPECIFIERS).join(', ')}
        multiple
        className="hidden"
        data-testid="upload-images"
        onChange={(e) => {
          if (e.target.files) {
            handleFiles(e.target.files);
            e.target.value = '';
          }
        }}
      />
      <DescriptionModal
        open={modalOpen}
        onSave={handleSaveDescriptions}
        onDismiss={handleDismissModal}
        allImages={images.map((img) => getImageUrl(img.uri))}
        initialImageIndex={editingIdx ?? 0}
        initialDescriptions={images.map((img) => img.description ?? '')}
      />
    </div>
  );
};
